import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';

import UltimatePagination from 'react-ultimate-pagination-bootstrap-3';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle, faSearch, faList, faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons';

import LoadingSpinner from "components/loading_spinner";

import PropertiesBulkDeleteModal from './modals/properties_bulk_delete_modal';
import PropertiesBulkTaggingModal from './modals/properties_bulk_tagging_modal';
import PropertiesDeleteGroupModal from './modals/properties_delete_group_modal';
import PropertiesDeleteSavedSearch from './modals/delete_saved_search_modal';
import PropertiesGroupFormModal from './modals/properties_group_form_modal';
import CopyRecordsModal from './modals/copy_records_modal';
import MoveRecordsModal from './modals/move_records_modal';

import PropertiesPageHeader from './header';
import MobileResearchFilters from '../filters/mobile_research_filters';
import PropertiesMapContainer from './properties_map_container';
import PropertiesMapList from './properties_map_list';
import PropertiesTable from './properties_table';
import ResearchFilterTopbar from '../filters/research_filter_topbar';
import DuplicatePropertiesView from './duplicates/duplicate_properties_view';

import { changePropertyPage } from '../store/actions/query';
import { retrieveGroups } from "../store/actions/properties";
import { retrieveRecords, delayRetrieveRecords } from '../store/actions/search';
import { switchTab, toggleAlert } from '../store/actions/ui';
import { resetSelectedProperties, resetSelectedPropertiesOnCurrentPage } from '../store/actions/select_list';
import { isDuplicateViewActive } from '../store/selectors/duplicates';

export class PropertiesTab extends React.Component {
  static propTypes = {
    currentPage: PropTypes.number.isRequired,
    currentTab: PropTypes.oneOf(['maps', 'properties']).isRequired,
    delayRetrieveRecords: PropTypes.func.isRequired,
    edgeCrm: PropTypes.bool,
    emptyStateHelpPath: PropTypes.string.isRequired,
    groups: PropTypes.array,
    initialFetch: PropTypes.bool,
    isDuplicateViewActive: PropTypes.bool,
    isFetchingProperties: PropTypes.bool,
    pageSize: PropTypes.number.isRequired,
    properties: PropTypes.array.isRequired,
    propertyIds: PropTypes.array.isRequired,
    resetSelection: PropTypes.func.isRequired,
    resetSelectionOnCurrentPage: PropTypes.func.isRequired,
    retrieveGroups: PropTypes.func.isRequired,
    retrieveRecords: PropTypes.func.isRequired,
    selectedGroupId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    selectedPropertyIds: PropTypes.arrayOf(PropTypes.number).isRequired,
    svnMember: PropTypes.bool,
    switchTab: PropTypes.func.isRequired,
    toggleAlert: PropTypes.func.isRequired,
    totalCount: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    totalUngroupedCount: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired
  };

  scrollPanel = React.createRef();

  state = {
    displayMobileFilters: false
  }

  componentDidMount() {
    this.props.resetSelection();
  }

  findSelectedGroup() {
    const { groups, selectedGroupId } = this.props;
    if (selectedGroupId) {
      return groups.find(group => group.id === selectedGroupId);
    }
  }

  handlePageChange = (page) => {
    const { onPageChange, retrieveRecords } = this.props;
    onPageChange(page);
    retrieveRecords();
    this.scrollToTop();
  };

  scrollToTop() {
    this.scrollPanel.current.scrollTop = 0;
  }

  mobileTabClass(tab) {
    const { currentTab } = this.props;

    return classNames({
      'mobile-icons': true,
      'active': currentTab === tab
    });
  }

  handleDisplayMobileFilters = () => this.setState({ displayMobileFilters: true });
  handleCloseMobileFilters = () => this.setState({ displayMobileFilters: false });

  render() {
    if (this.props.isDuplicateViewActive) {
      return <DuplicatePropertiesView />;
    }

    return (
      <React.Fragment>
        <PropertiesPageHeader />
        {this.renderFilterTopBar()}
        {this.renderMobileTabIcons()}
        <div className="table-content">
          <div className="main-content" ref={this.scrollPanel}>
            {this.renderActiveTab()}
          </div>
        </div>
        {this.state.displayMobileFilters && this.renderMobileFilters()}
        <PropertiesDeleteGroupModal />
        <PropertiesGroupFormModal />
        <PropertiesBulkTaggingModal />
        <PropertiesBulkDeleteModal />
        <PropertiesDeleteSavedSearch />
        <CopyRecordsModal />
        <MoveRecordsModal />
      </React.Fragment>
    );
  }

  renderMobileFilters() {
    const { totalCount } = this.props;

    return (
      <div className="visible-xs visible-sm mobile-content">
        <MobileResearchFilters
          propertyCount={totalCount}
          onCloseFilters={this.handleCloseMobileFilters}
        />
      </div>
    );
  }

  renderFilterTopBar() {
    const {
      currentPage,
      pageSize,
      properties,
      totalCount,
    } = this.props;

    const pageData = {
      currentPage: currentPage,
      itemCount: properties.length,
      itemSingularLabel: "property",
      pageSize: pageSize,
      totalCount: totalCount
    };


    return (
      <ResearchFilterTopbar
        edgeCrm={this.props.edgeCrm}
        pageData={pageData}
        propertyCount={totalCount}
        onDisplayFilters={this.handleDisplayMobileFilters}
      />
    );
  }

  renderActiveTab() {
    const { currentTab } = this.props;

    switch (currentTab) {
    case 'properties':
      return (
        <React.Fragment>
          {this.renderResults()}
          {this.renderPagination()}
        </React.Fragment>
      );
    case 'maps':
      return (
        <div className="maps-container">
          <PropertiesMapList onPageChange={this.handlePageChange} />
          <div className="maps-view">
            <PropertiesMapContainer name="maps" />
          </div>
        </div>
      );
    }
  }

  renderResults() {
    const {
      isFetchingProperties,
      properties,
      propertyIds,
      resetSelection,
      resetSelectionOnCurrentPage,
      svnMember,
      emptyStateHelpPath
    } = this.props;

    if (isFetchingProperties) {
      return <LoadingSpinner/>;
    } else if (properties.length > 0) {
      return (
        <PropertiesTable
          name = "properties"
          properties={properties}
          propertyIds={propertyIds}
          resetSelection={resetSelection}
          resetSelectionOnCurrentPage={resetSelectionOnCurrentPage}
        />
      );
    } else {
      return (
        <div className="no-results-panel">
          <FontAwesomeIcon className="text-muted my-4" icon={faSearch} size="5x"/>
          <h4 className="mt-4">
            <strong className="text-lg">No results found</strong>
          </h4>
          <p className="text-muted mb-4">Search with different criteria or create a New Property</p>
          { svnMember &&
            <a className="mt-4" href={emptyStateHelpPath}>
              <FontAwesomeIcon icon={faQuestionCircle} />
              <span className="ml-2">How to use your Property Database</span>
            </a>
          }
        </div>
      );
    }
  }

  renderMobileTabIcons() {
    const { switchTab } = this.props;

    return (
      <div className="visible-xs visible-sm">
        <div className="mobile-icons-container">
          <div
            className={this.mobileTabClass('maps')}
            onClick={() => switchTab('maps')}
          >
            <FontAwesomeIcon className="right-buffer-sm" icon={faMapMarkerAlt} /> Map
          </div>
          <div
            className={this.mobileTabClass('properties')}
            onClick={() => switchTab('properties')}
          >
            <FontAwesomeIcon className="right-buffer-sm" icon={faList} /> List
          </div>
        </div>
      </div>
    );
  }

  renderPagination() {
    const { properties, totalCount, currentPage, totalPages } = this.props;

    if (properties.length < totalCount) {
      return (
        <div className="text-center">
          <UltimatePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onChange={this.handlePageChange}
          />
        </div>
      );
    }
  }
}

const mapStateToProps = state => ({
  ...state.properties,
  ...state.search.meta,

  currentTab: state.ui.currentTab,
  edgeCrm: state.features.edgeCrm,
  emptyStateHelpPath: state.paths.emptyStateHelp,
  initialFetch: state.search.initialFetchComplete,
  isDuplicateViewActive: isDuplicateViewActive(state),
  isFetchingProperties: state.search.isFetching,
  properties: state.search.properties,
  selectedGroupId: state.query.researchGroupId,
  selectedPropertyIds: state.selectList.selectedPropertyIds,
  svnMember: state.ui.svnMember,
});

const mapDispatchToProps = {
  delayRetrieveRecords,
  retrieveGroups,
  retrieveRecords,
  switchTab,
  toggleAlert,

  onPageChange: changePropertyPage,
  resetSelection: resetSelectedProperties,
  resetSelectionOnCurrentPage: resetSelectedPropertiesOnCurrentPage
};

export default connect(mapStateToProps, mapDispatchToProps)(PropertiesTab);
